import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Search, RefreshCw, Download, CheckCircle, AlertCircle, XCircle, Loader2 } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface PaymentHistoryListProps {
  organisationId: string;
  stripeCustomerId?: string;
}

export const PaymentHistoryList: React.FC<PaymentHistoryListProps> = ({ 
  organisationId,
  stripeCustomerId
}) => {
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('all'); // 'all', 'last30', 'last90', 'custom'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Load payment history when component mounts or when filters change
  useEffect(() => {
    if (organisationId && stripeCustomerId) {
      loadPaymentHistory();
    }
  }, [organisationId, stripeCustomerId, dateFilter, startDate, endDate]);
  
  // Filter payments based on search query
  const filteredPayments = payments.filter(payment => {
    const searchLower = searchQuery.toLowerCase();
    return (
      (payment.invoice_id && payment.invoice_id.toLowerCase().includes(searchLower)) ||
      (payment.payment_intent_id && payment.payment_intent_id.toLowerCase().includes(searchLower)) ||
      (payment.status && payment.status.toLowerCase().includes(searchLower))
    );
  });
  
  // Calculate date range based on filter selection
  const getDateRange = () => {
    const now = new Date();
    let start = '';
    let end = now.toISOString().split('T')[0]; // today
    
    switch (dateFilter) {
      case 'last30':
        const last30 = new Date();
        last30.setDate(now.getDate() - 30);
        start = last30.toISOString().split('T')[0];
        break;
      case 'last90':
        const last90 = new Date();
        last90.setDate(now.getDate() - 90);
        start = last90.toISOString().split('T')[0];
        break;
      case 'custom':
        start = startDate;
        end = endDate || end;
        break;
      default: // 'all'
        start = '';
        end = '';
    }
    
    return { start, end };
  };
  
  // Load payment history directly from Supabase
  const loadPaymentHistory = async () => {
    if (!stripeCustomerId) return;
    
    setLoading(true);
    try {
      const { start, end } = getDateRange();
      console.log(`Loading payment history for customer ${stripeCustomerId} from ${start || 'all time'} to ${end || 'present'}`);
      
      // Check if the sms_payments table exists, if not create it
      try {
        const { data: tableExists } = await supabase.rpc('create_sms_payments_if_not_exists');
        console.log('SMS payments table check result:', tableExists);
      } catch (tableError) {
        console.warn('Error checking/creating sms_payments table:', tableError);
        // Continue - we'll attempt the query anyway
      }
      
      // Build query with proper filters
      let query = supabase
        .from('sms_payments')
        .select('*')
        .eq('stripe_customer_id', stripeCustomerId)
        .order('payment_date', { ascending: false });
        
      // Apply date filters if provided
      if (start) {
        query = query.gte('payment_date', start);
      }
      
      if (end) {
        query = query.lte('payment_date', end);
      }
      
      // Execute query
      const { data, error } = await query;
      
      if (error) {
        throw new Error(`Database error: ${error.message}`);
      }
      
      if (data && data.length > 0) {
        console.log(`Found ${data.length} payment records from database`);
        setPayments(data);
      } else {
        console.log('No payment records found in database');
        setPayments([]);
      }
    } catch (error) {
      console.error('Error loading payment history:', error);
      toast({
        title: "Failed to load payment history",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive"
      });
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Format date for display
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    } catch (error) {
      return dateString;
    }
  };
  
  // Format currency for display
  const formatCurrency = (amount: number, currency = 'NZD') => {
    if (amount === undefined || amount === null) return 'N/A';
    
    return new Intl.NumberFormat('en-NZ', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };
  
  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'paid':
      case 'succeeded':
        return (
          <Badge className="bg-green-500 hover:bg-green-600">
            <CheckCircle className="mr-1 h-3 w-3" />
            {status}
          </Badge>
        );
      case 'failed':
        return (
          <Badge variant="destructive">
            <XCircle className="mr-1 h-3 w-3" />
            {status}
          </Badge>
        );
      case 'pending':
        return (
          <Badge variant="outline" className="border-amber-500 text-amber-500">
            <AlertCircle className="mr-1 h-3 w-3" />
            {status}
          </Badge>
        );
      default:
        return (
          <Badge variant="outline">
            {status || 'Unknown'}
          </Badge>
        );
    }
  };
  
  // Export payment data as CSV
  const exportPaymentData = () => {
    if (filteredPayments.length === 0) {
      toast({
        title: "No data to export",
        description: "There are no payment records to export.",
        variant: "default"
      });
      return;
    }
    
    try {
      // Create CSV content
      let csvContent = "Invoice ID,Payment Intent ID,Date,Amount,Status,Attempt Count\n";
      
      filteredPayments.forEach(payment => {
        csvContent += `"${payment.invoice_id || ''}","${payment.payment_intent_id || ''}","${payment.payment_date || ''}","${payment.amount || 0}","${payment.status || ''}","${payment.attempt_count || 0}"\n`;
      });
      
      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      
      link.setAttribute('href', url);
      link.setAttribute('download', `payment-history-${organisationId}-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast({
        title: "Export Successful",
        description: "Payment history has been downloaded",
      });
    } catch (error) {
      console.error('Error exporting payment data:', error);
      toast({
        title: "Export Failed",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Payment History</CardTitle>
          <CardDescription>
            View and manage SMS payment history for this organisation
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Filters and search */}
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground/70" />
                <Input
                  placeholder="Search invoices..."
                  className="pl-8"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              
              <div className="flex flex-wrap gap-4">
                <Select value={dateFilter} onValueChange={setDateFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter by date" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All time</SelectItem>
                    <SelectItem value="last30">Last 30 days</SelectItem>
                    <SelectItem value="last90">Last 90 days</SelectItem>
                    <SelectItem value="custom">Custom range</SelectItem>
                  </SelectContent>
                </Select>
                
                {dateFilter === 'custom' && (
                  <>
                    <Input
                      type="date"
                      className="w-32"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      placeholder="Start date"
                    />
                    <Input
                      type="date"
                      className="w-32"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      placeholder="End date"
                    />
                  </>
                )}
                
                <Button 
                  variant="outline" 
                  onClick={loadPaymentHistory}
                  disabled={loading || !stripeCustomerId}
                >
                  <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                
                <Button
                  variant="outline"
                  onClick={exportPaymentData}
                  disabled={filteredPayments.length === 0}
                >
                  <Download className="mr-2 h-4 w-4" />
                  Export
                </Button>
              </div>
            </div>
            
            {/* Payment history table */}
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                <span className="ml-2">Loading payment history...</span>
              </div>
            ) : stripeCustomerId ? (
              filteredPayments.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice ID</TableHead>
                      <TableHead className="hidden md:table-cell">Payment Intent</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="hidden lg:table-cell">Attempts</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPayments.map((payment, index) => (
                      <TableRow key={payment.id || `payment-${index}`}>
                        <TableCell className="font-medium">
                          {payment.invoice_id || 'N/A'}
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-xs truncate max-w-[150px]">
                          {payment.payment_intent_id || 'N/A'}
                        </TableCell>
                        <TableCell>
                          {formatDate(payment.payment_date)}
                        </TableCell>
                        <TableCell>
                          {formatCurrency(payment.amount, payment.currency)}
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(payment.status)}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">
                          {payment.attempt_count || 1}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="py-8 text-center border rounded-lg">
                  <AlertCircle className="mx-auto h-12 w-12 text-muted-foreground" />
                  <h3 className="mt-4 text-lg font-medium">No Payment History</h3>
                  <p className="mt-2 text-sm text-muted-foreground">
                    No payment records were found for the selected filters.
                  </p>
                </div>
              )
            ) : (
              <div className="py-8 text-center border rounded-lg">
                <AlertCircle className="mx-auto h-12 w-12 text-muted-foreground" />
                <h3 className="mt-4 text-lg font-medium">No Stripe Integration</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  This organisation doesn't have a Stripe customer mapping yet.
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};